import { Link } from 'react-router'
import {
  Base,
  useTheme,
  ReverCard,
  ReverLoading
} from '@itsrever/design-system'
import { useTranslation } from 'react-i18next'
import { HorizontalBarChart } from '../horizontal-bar-chart'
import { ProductCard } from '../product-card'
import { usePortalTranslate } from '@/hooks'
import { useProductMetrics } from '@/application/queries'
import { NoData } from '@/ui/shared'
import { useProductReturnReasonsShown } from '@/hooks/use-scope-platform'

interface ReturnAmountsProps {
  fromDate: string | undefined
  toDate: string | undefined
}

const ReturnAmounts: React.FC<ReturnAmountsProps> = ({ fromDate, toDate }) => {
  const { t } = useTranslation()

  const { trans } = usePortalTranslate({})

  const theme = useTheme()

  const { products, reasons, totalItemCount, loadingProductMetrics } =
    useProductMetrics(fromDate, toDate)

  const showReturnReasons = useProductReturnReasonsShown()

  return (
    <>
      <div className="w-full max-w-[1000px] p-4">
        <ReverCard className="mb-4 p-4" fullWidth>
          <div className="mb-4 flex items-center self-start">
            <p className="mr-1 self-start">
              {t('product_analytics.top_5_products')}
            </p>
            <Base.InfoTooltip
              label={t('product_analytics.about_top_5_products')}
              content={t('product_analytics.top_5_products_tooltip')}
            />
            <Link to={'/analytics/all-products'}>
              <p
                className="ml-2 text-sm"
                style={{ color: theme.colors.primary.main }}
              >
                ({t('product_analytics.see_more')})
              </p>
            </Link>
          </div>
          {loadingProductMetrics ? (
            <ReverLoading />
          ) : products.length > 0 ? (
            <div className="grid grid-cols-[repeat(5,_minmax(0,_1fr))] gap-4">
              {products.map((product) => {
                return ProductCard(product)
              })}
            </div>
          ) : (
            <NoData />
          )}
        </ReverCard>
        {showReturnReasons ? (
          <ReverCard className="flex flex-col items-center p-4" fullWidth>
            <div className="flex items-center self-start">
              <p className="mr-1 self-start">
                {t('product_analytics.split_reasons')}
              </p>
              <Base.InfoTooltip
                label={t('product_analytics.about_split_reasons')}
                content={t('product_analytics.split_reasons_tooltip')}
              />
            </div>
            {loadingProductMetrics ? (
              <ReverLoading />
            ) : reasons.length > 0 ? (
              <div className="w-[800px]">
                <HorizontalBarChart
                  labels={reasons.map((r) => trans(`select_reason_${r.key}`))}
                  values={reasons.map((r) => r.totalReturnedItems)}
                  total={totalItemCount}
                />
              </div>
            ) : (
              <NoData />
            )}
          </ReverCard>
        ) : null}
      </div>
    </>
  )
}

export default ReturnAmounts
